<script setup>
  import {inject, onBeforeUnmount, reactive, ref, useId} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentPatientDoesNotMatch from "@components/PatientSelect/PatientDoesNotMatch.vue";
  import {router} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import resetState from "@utils/Helpers/ResetState.js";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "ComponentJsonImportDialog",
  });

  defineProps({
    title: {
      type: String,
      default: "Neue Variante aus JSON-Datei erstellen",
    },
  });

  const privacy = inject("$privacy");

  const refDialog = ref(null);
  const textInputId = useId();

  const initialState = {
    status: "form",
    error: {type: null, text: null},
    patient: null, // patient to import
    currentPatient: null, // when dialog opens within a patient detail view
    parsedJson: null,
    fileDragging: false,
  };

  const state = reactive({...initialState});

  onBeforeUnmount(() => {
    close();
  });

  const processFileUpload = (event) => {
    processFileList(event.target.files);
  };

  const processFileDrop = (event) => {
    state.fileDragging = false;
    processFileList(event.dataTransfer.files);
  };

  const processFileList = async (fileList) => {
    if (fileList.length !== 1) {
      return setError("error", "Bitte laden Sie jeweils nur eine JSON-Datei hoch.");
    }

    const item = fileList.item(0);

    if (item.type !== "application/json") {
      return setError("error", "Bitte laden Sie nur JSON-Dateien hoch.");
    }

    try {
      let text = await item.text();

      if (text.includes("�")) {
        text = new TextDecoder("iso-8859-1").decode(await item.arrayBuffer());
      }

      processJson(text);
    } catch (error) {
      console.error("File processing error:", error);

      return setError("error", "Beim Verarbeiten der Datei ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    }
  };

  const processTextInput = debounce((event) => {
    processJson(event.target.value);
  }, 1000);

  const reset = (not = []) => {
    Object.entries(initialState).forEach(([key, value]) => {
      if (!not.includes(key)) {
        state[key] = resetState(value);
      }
    });
  };

  const open = (patient = null) => {
    reset();
    state.currentPatient = patient;
    refDialog.value.open();
  };

  const close = () => {
    refDialog.value?.close();
    reset();
  };

  const performImportToCurrentPatient = () => {
    saveAndOpenRecord({patient: state.currentPatient.id});
  };

  const setError = (type, text) => {
    state.error.type = type;
    state.error.text = text;
  };

  const clearError = () => {
    state.error.type = null;
    state.error.text = null;
  };

  const processJson = async (json) => {
    if (json.trim().length === 0) {
      return;
    }

    clearError();

    try {
      state.parsedJson = JSON.parse(json.trim());
      state.patient = state.parsedJson?.patient;
    } catch (error) {
      console.error("JSON parse error:", error);

      return setError("error", "Bitte laden Sie nur gültige JSON-Dateien hoch.");
    }

    if (
      state.currentPatient &&
      (state.currentPatient.firstname !== state.patient?.firstname ||
        state.currentPatient.lastname !== state.patient?.lastname ||
        state.currentPatient.gender !== state.patient?.gender ||
        state.currentPatient.birthdate !== state.patient?.birthdate ||
        state.currentPatient.insurancenumber !== state.patient?.insurancenumber)
    ) {
      // we are within a patient detail view, but the imported bmp
      // does not match the current patient
      state.status = "patientDoesNotMatch";
    } else {
      if (state.currentPatient) {
        saveAndOpenRecord({patient: state.currentPatient.id});
      } else {
        saveAndOpenRecord();
      }
    }
  };

  const saveAndOpenRecord = async (routeParameters = {}) => {
    state.status = "saving";

    state.parsedJson.patient = await privacy.encryptPatient(state.parsedJson.patient);

    router.post(route("init-from-json", {origin: "json", ...routeParameters}), state.parsedJson, {
      preserveState: false,
      preserveScroll: false,
      onSuccess: () => close(),
      onError: () => {
        reset(["currentPatient"]);
        setError("error", "Beim Importieren ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
        state.status = "form";
      },
    });
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refDialog" :dialog-title="title" dialog-width="medium" :test-id="getTestId('json-import')">
    <template #content>
      <div v-if="state.status === 'form'" class="mt-5 flex flex-col gap-3">
        <ComponentAlert
          v-if="state.error.type && state.error.text"
          :type="state.error.type"
          :test-id="getTestId('json-import-dialog')"
        >
          {{ state.error.text }}
        </ComponentAlert>

        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- drag-and-drop has no keyboard equivalent; the file input inside this label is visually hidden but focusable and reaches the same upload path -->
        <label
          class="flex cursor-pointer flex-col items-center justify-center gap-3 rounded border border-dashed border-gray-700/25 px-6 py-10 hover:border-mcred"
          :class="{'border-mcred!': state.fileDragging}"
          :data-test="getTestId('json-import-dialog-upload-input-label')"
          @drag.prevent="state.fileDragging = true"
          @dragend.prevent="state.fileDragging = false"
          @dragenter.prevent="state.fileDragging = true"
          @dragover.prevent="state.fileDragging = true"
          @dragleave.prevent="state.fileDragging = false"
          @drop.prevent="processFileDrop"
        >
          <ComponentIcon class="text-5xl">upload_file</ComponentIcon>

          <span class="text-sm">JSON-Datei per Drag-and-Drop oder Klicken hochladen</span>

          <input
            type="file"
            class="sr-only"
            accept="application/json"
            :data-test="getTestId('json-import-dialog-upload-input')"
            @change.prevent="processFileUpload"
          />
        </label>

        <label :for="textInputId">oder JSON-Code manuell einfügen</label>

        <textarea
          :id="textInputId"
          class="resize-y rounded border border-gray-700/25"
          placeholder="Hier JSON-Code einfügen"
          rows="3"
          :data-test="getTestId('json-import-dialog-textarea')"
          @input="processTextInput"
        />
      </div>

      <div v-else-if="state.status === 'patientDoesNotMatch'">
        <ComponentPatientDoesNotMatch
          :current-patient="state.currentPatient"
          :patient="state.patient"
          :test-id="getTestId('json-import-dialog')"
        />
      </div>

      <div v-else-if="state.status === 'saving'" class="flex flex-col items-center justify-center py-10">
        <div class="py-4 text-lg">Wird gespeichert...</div>
        <MedSpinner size="2xl" :data-test="getTestId('json-import-dialog-saving-spinner')" />
      </div>

      <div v-else class="flex flex-col items-center justify-center py-10">
        <MedSpinner size="2xl" :data-test="getTestId('json-import-dialog-loading-spinner')" />
      </div>
    </template>

    <template #actions>
      <template v-if="state.status === 'patientDoesNotMatch'">
        <MedButton
          variant="primary"
          :data-test="getTestId('bmp-import-dialog-perform-import-to-current-patient-button')"
          @click="performImportToCurrentPatient"
        >
          In bestehenden Patienten importieren
        </MedButton>
      </template>

      <MedButton :data-test="getTestId('json-import-dialog-close-button')" @click="close">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
